import React, { useEffect, useRef, useState } from 'react';
import { Grid } from '@naf/grid';
import { Text, TextVariant } from '@naf/text';
import { useAuth0 } from '@auth0/auth0-react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { CenterProductArticleType } from '@appTypes/centerProductArticleType';
import { useWindowLocation } from '@hooks/Window/useWindowLocation';
import { LayoutWithMainContent } from '@components/layout/Layout';
import BreadCrumb from '@components/breadcrumb/BreadCrumb';
import BlockContent from '@components/block-content/BlockContent';
import { BlockContentContext } from '@components/block-content/BlockContent.utils';
import useSelector from '@redux/typedHooks';
import { useAuth0Token } from '@hooks/useAuth0Token';
import { useSessionStorage } from '@hooks/useSessionStorage';
import { InternalSearchCardType } from '@components/layout/InternalSearchMetaData';
import { MetaData } from '@components/metaData/MetaData';
import { actions as CenterProductActions } from '@redux/modules/centerProducts';
import { actions as MyMembershipActions } from '@redux/modules/myMembership';
import { actions as vehicleActions } from '@redux/modules/myVehicles';
import FeedbackSurvey from '@components/feedbacksurvey/FeedbackSurvey';
import TableOfContents from '@components/TableOfContents/TableOfContents';
import { useTrackCTA } from '@utils/Tracking/CtaClick/TrackCtaClick';
import { ProductPrice } from './ProductPrice';
import { ServiceCalculator } from '../serviceCalculator/ServiceCalculator';
import ServiceCalculatorProvider from '../serviceCalculator/context/ServiceCalculatorContext';
import { ProductCard } from './components/ProductCard/ProductCard';
import { VehicleSelecter } from './components/VehicleSelecter/VehicleSelecter';
import { handleSelectedVehicleClick, WORKSHOP_INTERNAL_PRODUCT_MAP } from './NafProductArticle.utils';
import S from './NafProductArticle.styles';

export const path = ':productSlug';

interface Props {
  centerProductArticle: CenterProductArticleType;
  productSlug: string;
}

export const NafProductArticle = ({ centerProductArticle, productSlug }: Props) => {
  const dispatch = useDispatch();
  const trackCTA = useTrackCTA();
  const { simpleToken } = useAuth0Token();
  const { isAuthenticated, isLoading } = useAuth0();
  const [centerId] = useSessionStorage('centerId', undefined);
  const { products: centerProducts, isUpdating } = useSelector((state) => state.centerProducts);
  const {
    title,
    customDisplay,
    ingress,
    body,
    products,
    tableOfContents,
    relatedProductArticles,
    seoConfig,
    meta,
    metaDisplay,
  } = (centerProductArticle as CenterProductArticleType) || {};
  const seoTitle = seoConfig?.title || title;
  const windowPath = useWindowLocation();
  const location = useLocation();
  const productArticleRef = useRef<HTMLDivElement | null>(null);
  const customerInformation = useSelector((state) => state.myMembership.customerInformation.data);
  const isUpdatingVehicles = useSelector((state) => state.myVehicles.vehicleList.isUpdating);
  const myVehicles = useSelector((state) => state.myVehicles.vehicleList.data);
  const workshopBookingLink = useSelector((state) => state.application.nafcenterWorkshopBookingUrl);
  const isWorkshopPage = products && (products[0]?.internalProductId === '1' || products[0]?.internalProductId === '2');
  const productPageType = WORKSHOP_INTERNAL_PRODUCT_MAP[products[0]?.internalProductId] || null;

  const [shouldRenderServiceCalculator, setShouldRenderServiceCalculator] = useState(false);

  useEffect(() => {
    if (products && products[0]?.name === 'ServiceCalculator' && location.state?.custom && customerInformation) {
      setShouldRenderServiceCalculator(true);
    }
  }, [customerInformation, location.state?.custom, products]);

  useEffect(() => {
    if (simpleToken && !customerInformation) {
      dispatch(MyMembershipActions.getCustomerInformation.request(simpleToken));
    }
  }, [simpleToken, dispatch, customerInformation]);

  useEffect(() => {
    if (centerId && productSlug) {
      dispatch(CenterProductActions.getCenterProducts.request(productSlug, { id: centerId as number }));
    }
  }, [productSlug, centerId, dispatch]);

  const hasCampaign =
    products &&
    products.length > 0 &&
    products.some(
      (value) =>
        (value.memberPrice && value.memberPrice.isCampaign) ||
        (value.nonMemberPrice && value.nonMemberPrice.isCampaign),
    );

  useEffect(() => {
    if (!myVehicles && simpleToken) {
      dispatch(vehicleActions.getMyVehicles.request(simpleToken));
    }
  }, [myVehicles, simpleToken, dispatch]);

  if (shouldRenderServiceCalculator) {
    return (
      <ServiceCalculatorProvider>
        <ServiceCalculator
          centerId={centerId}
          centerProductArticle={centerProductArticle}
          productSlug={productSlug}
          isMember={customerInformation.membership?.isValid}
        />
      </ServiceCalculatorProvider>
    );
  }

  return (
    <LayoutWithMainContent
      title={title}
      description={ingress || seoConfig?.introduction || ''}
      imgUrl=""
      url={windowPath}
      gtmArgs={{
        page_type: 'NafCenterProductArticle',
        pageCategory: 'NafCenterProductArticle',
        contentId: productSlug,
      }}
      isHiddenFromSearch={seoConfig?.isHiddenFromSearch}
      seoTitle={seoTitle}
      seoDescription={seoConfig?.introduction || undefined}
      internalSearchMetaData={{ cardType: InternalSearchCardType.NafCenterProduct }}
    >
      <BreadCrumb paddingTop={false} />
      <S.HeaderColorBox />
      <Grid>
        <S.HeaderGridCol s="12" m="12" l="12" xl="12">
          <Text variant={TextVariant.Display}>{title}</Text>
          {hasCampaign && <S.StyledTag text="Kampanje" color="secondary" shade="light" />}
        </S.HeaderGridCol>
        {products && products.length > 0 && !isUpdating && <ProductPrice products={centerProducts || products} />}
        {customDisplay && customDisplay.length > 0 && (
          <S.CustomDisplayCol s="12" m="12" l="8" xl="8">
            <BlockContent value={customDisplay} />
          </S.CustomDisplayCol>
        )}
        <S.BodyCol s="12" m="12" l="12" xl="12">
          {tableOfContents?.enabled && body && (
            <TableOfContents tagType="h2" title={tableOfContents?.title} parentElement={productArticleRef} />
          )}
          {ingress && <S.Ingress>{ingress}</S.Ingress>}
          {isWorkshopPage && productPageType && (
            <VehicleSelecter
              isAuthenticated={isAuthenticated}
              isLoading={isLoading}
              isUpdatingVehicles={isUpdatingVehicles}
              myVehicles={myVehicles}
              isWorkshopPage={isWorkshopPage}
              pageType={productSlug}
              onSelectedVehicleClick={(licensePlateNumber, options) =>
                handleSelectedVehicleClick({
                  licensePlateNumber,
                  skipCtaTracking: options?.skipCtaTracking,
                  trackCTA,
                  workshopBookingLink,
                  centerId,
                  bookingType: productPageType?.bookingType,
                })
              }
            />
          )}
          {body && body.length > 0 && (
            <S.Body ref={productArticleRef}>
              <BlockContent
                context={BlockContentContext.ProductArticle}
                value={body.filter((block) => block._type !== 'quoteComponent')}
              />
            </S.Body>
          )}
          <MetaData meta={meta} metaDisplay={metaDisplay} />
        </S.BodyCol>
        {body && body.some((block) => block && block._type === 'quoteComponent') && (
          <S.QuoteCol s="12" m="12" l="12" xl="12">
            <BlockContent value={body.filter((block) => block._type === 'quoteComponent')} />
          </S.QuoteCol>
        )}
        {relatedProductArticles && relatedProductArticles.length > 0 && (
          <ProductCard otherProducts={relatedProductArticles.slice(0, 3)} />
        )}
        <FeedbackSurvey contentUrl={windowPath} />
      </Grid>
    </LayoutWithMainContent>
  );
};
